import React, { useEffect, useState, useCallback } from 'react';

import classNames from 'classnames';
import { useRanger } from 'react-ranger';
import { ListGroup } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import _round from 'lodash/round';
import { boolean } from 'boolean';

import { useAppSelector } from 'hooks';
import {
  getFormattedValueForMeasurementClass,
  getUnitForMeasurementClass
} from 'utils/MeasurementsStoreFunctions';
import { getOperator } from 'constants/constants';
import { BellIcon } from 'assets/icons';
import { getRangeColorRefRanges } from 'utils/Icons';
import { getMeasurementLimits, getMeasurementsOptions } from 'utils/Measurements';

interface IReferenceRangeProps {
  data: ICarePlanReferenceRange;
}

const ReferenceRange = ({ data }: IReferenceRangeProps) => {
  const intl = useIntl();
  const measurementClasses =
    useAppSelector((state) => state.settings.citc?.measurementClasses) || [];
  const measurementClassOptions = measurementClasses
    ? getMeasurementsOptions(measurementClasses)
    : {};

  const prefUnitValue = data.zones[0]?.unit || '';
  const measureMinMax = getMeasurementLimits(
    measurementClasses,
    data.measurement_class,
    prefUnitValue
  );

  const min = measurementClassOptions[data.measurement_class]
    ? Math.min(
        ...measurementClassOptions[data.measurement_class].map((choice) => parseInt(choice.value))
      )
    : measureMinMax && measureMinMax.units && prefUnitValue
      ? Number(
          measureMinMax.units.find((unitDetail) => unitDetail[prefUnitValue])?.[prefUnitValue]?.min
        )
      : 0;
  const max = measurementClassOptions[data.measurement_class]
    ? Math.max(
        ...measurementClassOptions[data.measurement_class].map((choice) => parseInt(choice.value))
      )
    : measureMinMax && measureMinMax.units && prefUnitValue
      ? Number(
          measureMinMax.units.find((unitDetail) => unitDetail[prefUnitValue])?.[prefUnitValue]?.max
        )
      : 0;
  const showSegments =
    (measureMinMax || measurementClassOptions[data.measurement_class]) &&
    !['cumulativeSleep', 'totalSleep'].includes(data.measurement_class);

  const getDefaultValue = useCallback(() => {
    const defaultValue = [];
    if (showSegments) {
      for (const zone of data.zones) {
        zone.high &&
          defaultValue.push(
            getFormattedValueForMeasurementClass(zone.high, data.measurement_class)
          );
      }
    }
    return defaultValue;
  }, [data, showSegments]);

  const [values, setValues] = useState(getDefaultValue());
  const [hoverId, setHoverIndex] = useState<string | null>(null);

  const {
    getTrackProps,
    ticks,
    segments
    // handles
  } = useRanger({
    min,
    max,
    stepSize: 1,
    tickSize: (max - min) / 10,
    values,
    onChange: setValues
  });

  const formattedRange = (zone: ICarePlanReferenceRangeZone) => {
    const unit = getUnitForMeasurementClass(data.measurement_class, zone.unit, intl);

    if (zone.low_operator && zone.high_operator) {
      const low = getFormattedValueForMeasurementClass(zone.low, data.measurement_class);
      const high = getFormattedValueForMeasurementClass(zone.high, data.measurement_class);
      return `${getOperator(zone.low_operator)} ${low} ${getOperator(zone.high_operator)} ${high} ${unit}`;
    }

    if (zone.low_operator) {
      const low = getFormattedValueForMeasurementClass(zone.low, data.measurement_class);
      return `${getOperator(zone.low_operator)} ${low} ${unit}`;
    }

    if (zone.high_operator) {
      const high = getFormattedValueForMeasurementClass(zone.high, data.measurement_class);
      return `${getOperator(zone.high_operator)} ${high} ${unit}`;
    }

    return unit;
  };

  useEffect(() => {
    setValues(getDefaultValue());
  }, [getDefaultValue]);

  return (
    <div className="ranger">
      <ListGroup variant="flush" className="mt-4">
        {[...data.zones].reverse().map((zone, i: number) => (
          <ListGroup.Item
            data-test={`reference-range-${zone.label}-${data.measurement_class}`}
            className={classNames('legend', {
              hover: hoverId === zone.label
            })}
            key={i}
            onMouseEnter={() => setHoverIndex(zone.label)}
            onMouseLeave={() => setHoverIndex(null)}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <div
                  data-test={`reference-range-color-${zone.label}-${data.measurement_class}`}
                  className={'me-3 level ' + getRangeColorRefRanges(zone.color)}
                >
                  {boolean(zone.alert_enabled) && (
                    <BellIcon
                      data-test={`reference-range-alert-${zone.label}-${data.measurement_class}`}
                      className="icon text-white align-middle"
                    />
                  )}
                </div>
                <div>{intl.formatMessage({ id: zone.label })}</div>
              </div>
              <div
                data-test={`reference-range-value-${zone.label}-${data.measurement_class}`}
                className="font-weight-bolder"
              >
                {formattedRange(zone)}
              </div>
            </div>
          </ListGroup.Item>
        ))}
      </ListGroup>
      {showSegments && (
        <div
          data-test={`reference-range-segments-${data.measurement_class}`}
          className="track mb-4"
          {...getTrackProps()}
        >
          {ticks.map(({ value, getTickProps }) => (
            /* eslint-disable-next-line react/jsx-key */
            <div className="tick" {...getTickProps()}>
              <div className="tickLabel">{_round(value, 2)}</div>
            </div>
          ))}
          {segments.map(({ getSegmentProps }, i) => (
            /* eslint-disable-next-line react/jsx-key */
            <div
              data-test={`reference-range-segment-${data.zones[i]?.label}-${data.measurement_class}`}
              className={classNames(`${getRangeColorRefRanges(data.zones[i]?.color)} segment`, {
                hide: data.zones[i]?.label !== hoverId
              })}
              onMouseEnter={() => setHoverIndex(data.zones[i]?.label)}
              onMouseLeave={() => setHoverIndex(null)}
              {...getSegmentProps()}
            />
          ))}
          {/* {handles.map(({ value, active, getHandleProps }) => (
            <button
              {...getHandleProps({
                style: {
                  appearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                },
              })}
            >
              <div
                className={classNames('handle', {
                  active,
                })}
              >
                {value}
              </div>
            </button>
          ))} */}
        </div>
      )}
    </div>
  );
};

export default ReferenceRange;
